import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { enabledModules } from "@/components/AppShell";
import { CategoryPicker, CategoryChip } from "@/components/husholdning/CategoryPicker";
import { HOUSEHOLD_HOME, planTerms } from "@/lib/householdConstants";
import { toast } from "sonner";
import { House, BookOpenText, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Regnskapskategori/-konto på en regning (source="regning") eller faktura (source="faktura").
 * - Regning: viser standard (via koblingen fra Regninger-kategorien) og lar bruker overstyre. Uten kobling telles den som «Ukategorisert».
 * - Faktura: kategori velges manuelt per faktura; uten valg er fakturaen «venter på kategori» og holdes utenfor regnskapet.
 *
 * To modi:
 *  - lagrende (docId satt): valget lagres straks via PUT /household/assign og `onChanged(result)` kalles.
 *  - kontrollert (uten docId, f.eks. i fakturaskjemaet): valget sendes kun til `onChange(id|null)` og lagres av skjemaet selv.
 * Rendrer ingenting når Regnskap-modulen er av for virksomheten. Uten valgt kontoplan vises en kort melding med lenke.
 */
export const HouseholdCategoryField = ({ source, docId, value, regningerCategoryId, onChanged, onChange, className, compact = false, readOnly = false }) => {
  const { canWrite, isOwner, activeWorkspace } = useAuth();
  const [plan, setPlan] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const moduleOn = !isOwner || enabledModules(activeWorkspace).includes("husholdning");
  const controlled = !docId;

  useEffect(() => {
    if (!moduleOn) return undefined;
    let cancelled = false;
    api.get("/household/plan").then((r) => { if (!cancelled) setPlan(r.data); }).catch((e) => { if (!cancelled && e?.response?.status === 403) setHidden(true); });
    return () => { cancelled = true; };
  }, [moduleOn, activeWorkspace?.id, activeWorkspace?.chart_plan]);

  if (!moduleOn || hidden || !plan) return null;

  const isInvoice = source === "faktura";
  const kind = isInvoice ? "inntekt" : "utgift";
  const editable = canWrite && !readOnly;

  // Tomtilstand: virksomheten har ikke valgt kontoplan (brukerbeslutning: ingen plan settes automatisk)
  if (plan.needs_plan) {
    return (
      <div className={cn("space-y-1", className)} data-testid={`household-field-${source}`}>
        <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><BookOpenText className="h-3.5 w-3.5" /> Regnskap</p>
        <p className="text-xs text-muted-foreground" data-testid={`household-field-${source}-needs-plan`}>
          {plan.can_choose ? <>Virksomheten har ikke valgt kontoplan ennå. <Link to={HOUSEHOLD_HOME} className="underline font-medium" data-testid={`household-field-${source}-choose-link`}>Velg kontoplan</Link> for å føre {isInvoice ? "fakturaen som inntekt" : "regningen som utgift"}.</> : "Eieren har ikke valgt kontoplan for regnskapet ennå."}
        </p>
      </div>
    );
  }

  const t = planTerms(plan.standard);
  const all = [...plan.groups.flatMap((g) => [g, ...g.children]), ...plan.income];
  const find = (id) => all.find((c) => c.id === id) || null;
  let fallback = null;
  let fallbackHint = "";
  if (!isInvoice) {
    const m = plan.mapping.find((x) => x.category_id === regningerCategoryId);
    fallback = m?.household_category_id ? find(m.household_category_id) : null;
    fallbackHint = m ? (fallback ? `via Regninger-kategorien «${m.name}»` : `Regninger-kategorien «${m.name}» er ikke koblet`) : "regningen mangler kategori";
  }
  const chosen = value ? find(value) : null;
  const effective = chosen || fallback;
  const groupColor = effective ? (effective.parent_id ? find(effective.parent_id)?.color : effective.color) : undefined;
  const Icon = t.isHousehold ? House : BookOpenText;

  const change = async (id) => {
    if (controlled) { onChange?.(id); return; }
    setBusy(true);
    try {
      const r = await api.put("/household/assign", { source, id: docId, category_id: id });
      toast.success(id ? `${t.short}: ${r.data.category_code} ${r.data.category_name}` : isInvoice ? "Kategori fjernet – fakturaen venter på kategori" : "Bruker koblingen fra Regninger-kategorien igjen");
      onChanged?.(r.data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const hint = isInvoice
    ? (chosen ? `Telles som inntekt på ${chosen.code} ${chosen.name} i fakturamåneden.` : controlled ? `Valgfritt nå – uten ${t.income} vil fakturaen stå som «venter på kategori» og holdes utenfor ${t.regnskapOrd}.` : `Venter på ${t.income} – fakturaen telles ikke i ${t.regnskapOrd} før du velger.`)
    : (value ? "Overstyrt for denne regningen." : effective ? `Telles som ${effective.code} ${effective.name} (${fallbackHint}).` : `Telles som «Ukategorisert» – ${fallbackHint}.`);

  return (
    <div className={cn("space-y-1.5", className)} data-testid={`household-field-${source}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {t.short}{isInvoice ? ` · ${t.income}` : ""}</p>
        <Link to={`${HOUSEHOLD_HOME}/kategorier`} className="text-[11px] text-muted-foreground underline" data-testid={`household-field-${source}-plan-link`}>{t.isHousehold ? "Kategoriplan" : "Kontoplan"}</Link>
      </div>
      {editable ? (
        <CategoryPicker plan={plan} kind={kind} value={value || null} onChange={change} disabled={busy} placeholder={isInvoice ? `Velg ${t.income}` : "Velg kategori"} noneLabel={isInvoice ? "Ikke valgt (venter på kategori)" : fallback ? `Standard: ${fallback.code} ${fallback.name}` : "Standard (Ukategorisert)"} testId={`household-field-${source}-picker`} className={compact ? "h-9" : undefined} />
      ) : isInvoice && !chosen ? (
        <span className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs status-forfaller_snart" data-testid={`household-field-${source}-chip`}><Clock className="h-3 w-3" /> Venter på kategori</span>
      ) : (
        <CategoryChip code={effective?.code} name={effective?.name || "Ukategorisert"} color={groupColor} muted={!effective} testId={`household-field-${source}-chip`} />
      )}
      <p className={cn("text-[11px]", isInvoice && !chosen && !controlled ? "text-flag" : "text-muted-foreground")} data-testid={`household-field-${source}-hint`}>{hint}</p>
    </div>
  );
};
